import { useCallback, useEffect, useMemo, useState } from "react";
import { eliminar as eliminarFila, guardar as guardarFila, listar, listarReuniones, rpc } from "../../services/datos";
import Modal from "../../shared/components/Modal";
import QrRegistro from "../registro/QrRegistro";
import { confirmar } from "../../shared/avisos";
import Selector from "../../shared/components/Selector";
import Esqueleto from "../../shared/components/Esqueleto";
import {
  botonPeligro, botonPequeno, botonPrimario, botonSecundario, campo, etiqueta, fechaLarga, hora, hoyISO,
  nombreReunion,
} from "../../shared/estilos";

const vacio = { tipo_id: "", fecha: "", hora: "", titulo: "", tema: "" };

// Calendario: aquí se crean las reuniones. Todo lo demás (servidores, programa
// y asistencia) cuelga de una reunión, así que cada tarjeta lleva a esas pantallas.
function Reuniones({ irA }) {
  const [servicios, setServicios] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [turnos, setTurnos] = useState([]);
  const [vista, setVista] = useState("proximas");
  const [form, setForm] = useState(null);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [qr, setQr] = useState(null);
  const hoy = hoyISO();

  const cargar = useCallback(async () => {
    try {
      const [listaServicios, listaTipos, listaTurnos] = await Promise.all([
        listarReuniones(), listar("tipos_servicio", { orden: "nombre" }),
        listar("turnos_servicio", { select: "id, servicio_id" }),
      ]);
      setServicios(listaServicios);
      setTipos(listaTipos);
      setTurnos(listaTurnos);
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const servidoresPorReunion = useMemo(() => turnos.reduce((conteo, t) => {
    conteo[t.servicio_id] = (conteo[t.servicio_id] || 0) + 1;
    return conteo;
  }, {}), [turnos]);

  const visibles = useMemo(() => {
    const lista = servicios.filter((s) => (vista === "proximas" ? s.fecha >= hoy : s.fecha < hoy));
    return lista.sort((a, b) => (vista === "proximas" ? 1 : -1) * `${a.fecha}${a.hora || ""}`.localeCompare(`${b.fecha}${b.hora || ""}`));
  }, [servicios, vista, hoy]);

  const porMes = useMemo(() => visibles.reduce((grupos, s) => {
    const mes = fechaLarga(s.fecha, { month: "long", year: "numeric" });
    (grupos[mes] = grupos[mes] || []).push(s);
    return grupos;
  }, {}), [visibles]);

  const ejecutar = async (accion) => {
    setOcupado(true);
    setError("");
    setMensaje("");
    try {
      setMensaje(await accion());
      await cargar();
    } catch (e) {
      setError(e.message || "No se pudo completar la acción");
    } finally {
      setOcupado(false);
    }
  };

  const guardar = () => {
    if (!form.tipo_id || !form.fecha) {
      setError("Elige el tipo de reunión y la fecha.");
      return;
    }
    const datos = { ...form, hora: form.hora || null };
    ejecutar(async () => {
      if (form.id) {
        await guardarFila("servicios", form.id, datos);
        setForm(null);
        return "Reunión actualizada. Si cambió la fecha u hora, se avisó a los servidores que ya conocían su turno.";
      }
      await guardarFila("servicios", null, datos);
      setForm(null);
      return "Reunión creada. Las áreas con rotación automática ya tienen servidores asignados; recibirán el aviso 7 días antes.";
    });
  };

  const eliminar = async (servicio) => {
    if (!(await confirmar(`¿Eliminar ${nombreReunion(servicio)} del ${fechaLarga(servicio.fecha)}? `
      + "Se borrarán su asistencia y sus turnos, y se avisará a los servidores.", { aceptar: "Eliminar" }))) return;
    ejecutar(async () => {
      await eliminarFila("servicios", servicio.id);
      return "Reunión eliminada.";
    });
  };

  const generar = () => ejecutar(async () => {
    const r = await rpc("generar_reuniones");
    const partes = [`${r.creados.length} reunión(es) nueva(s)`];
    if (r.rotaciones?.length) partes.push(`servidores asignados en ${r.rotaciones.length} reunión(es)`);
    return [`${partes.join(" y ")}.`, ...(r.avisos || [])].join(" ");
  });

  const editar = (s) => setForm({
    id: s.id, tipo_id: String(s.tipo_id || ""), fecha: s.fecha || "", hora: hora(s.hora), titulo: s.titulo || "", tema: s.tema || "",
  });

  return (
    <div className="p-6">
      <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="titulo-pagina">Calendario</h2>
          <p className="text-gray-400 text-sm mt-1 max-w-2xl">
            Crea aquí cada reunión. Después, desde la misma tarjeta, programa a los servidores, prepara el programa y toma la asistencia.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={generar} disabled={ocupado} className={botonSecundario}
            title="Domingos 10:00, miércoles 19:00 y martes de oración 5:00">
            Completar este mes y el siguiente
          </button>
          <button onClick={() => { setError(""); setForm(vacio); }} className={botonPrimario}>Nueva reunión</button>
        </div>
      </div>

      {mensaje && <p className="mb-4 rounded border border-white/15 bg-white/5 p-3 text-sm text-gray-200">{mensaje}</p>}
      {error && !form && <p role="alert" className="rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-200 mb-4 p-3 text-sm">{error}</p>}

      <div className="mb-6 flex gap-2">
        {[["proximas", "Próximas"], ["pasadas", "Pasadas"]].map(([id, texto]) => (
          <button key={id} onClick={() => setVista(id)} aria-pressed={vista === id}
            className={`pestana ${vista === id ? "pestana-activa" : ""}`}>
            {texto}
          </button>
        ))}
      </div>

      {cargando ? (
        <Esqueleto texto="Cargando reuniones…" />
      ) : visibles.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-700 p-12 text-center">
          <p className="text-white font-semibold">{vista === "proximas" ? "No hay reuniones próximas" : "No hay reuniones pasadas"}</p>
          {vista === "proximas" && (
            <p className="mt-2 text-sm text-gray-400">Crea una con “Nueva reunión” o completa los domingos, miércoles y martes de oración de este mes y el siguiente.</p>
          )}
        </div>
      ) : (
        Object.entries(porMes).map(([mes, lista]) => (
          <section key={mes} className="mb-8">
            <h3 className="titulo-panel mb-3">{mes}</h3>
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
              {lista.map((s) => {
                const servidores = servidoresPorReunion[s.id] || 0;
                const yaPaso = s.fecha <= hoy;
                return (
                  <article key={s.id} className="panel flex gap-5 p-5">
                    <div className="w-14 shrink-0 text-center">
                      <p className="text-3xl font-bold leading-none text-white">{Number(s.fecha.slice(8, 10))}</p>
                      <p className="mt-1 text-gray-400 text-xs">
                        {fechaLarga(s.fecha, { weekday: "short" })}
                      </p>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-semibold">{nombreReunion(s)}</p>
                      <p className="mt-1 text-sm text-gray-400">
                        {hora(s.hora) || "Sin hora"}{s.tema ? ` · ${s.tema}` : ""}
                      </p>
                      <p className={`mt-2 text-xs ${servidores ? "text-gray-400" : "text-yellow-300"}`}>
                        {servidores ? `${servidores} servidor(es) programado(s)` : "Sin servidores programados"}
                      </p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button onClick={() => irA("servidores", { servicioId: s.id })} className={botonPequeno}>Servidores</button>
                        <button onClick={() => irA("programacion", { servicioId: s.id })} className={botonPequeno}>Programa</button>
                        {yaPaso && <button onClick={() => irA("asistencia", { servicioId: s.id })} className={botonPequeno}>Asistencia</button>}
                        {s.fecha === hoy && <button onClick={() => setQr(s)} className={botonPequeno}>QR de registro</button>}
                        <button onClick={() => { setError(""); editar(s); }} className={botonPequeno}>Editar</button>
                        <button onClick={() => eliminar(s)} disabled={ocupado} className={botonPeligro}>Eliminar</button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ))
      )}

      {qr && <QrRegistro servicio={qr} onCerrar={() => setQr(null)} />}

      {form && (
        <Modal titulo={form.id ? "Editar reunión" : "Nueva reunión"} onCerrar={() => setForm(null)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="reunion-tipo" className={etiqueta}>Tipo de reunión *</label>
              <Selector id="reunion-tipo" value={form.tipo_id} onChange={(e) => setForm({ ...form, tipo_id: e.target.value })} className={campo}>
                <option value="">Seleccionar...</option>
                {tipos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
              </Selector>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="reunion-fecha" className={etiqueta}>Fecha *</label>
                <input id="reunion-fecha" type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className={campo} />
              </div>
              <div>
                <label htmlFor="reunion-hora" className={etiqueta}>Hora</label>
                <input id="reunion-hora" type="time" value={form.hora} onChange={(e) => setForm({ ...form, hora: e.target.value })} className={campo} />
              </div>
            </div>
            <div>
              <label htmlFor="reunion-titulo" className={etiqueta}>Título (opcional)</label>
              <input id="reunion-titulo" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} className={campo} placeholder="Ej: Noche de adoración" />
            </div>
            <div>
              <label htmlFor="reunion-tema" className={etiqueta}>Tema (opcional)</label>
              <input id="reunion-tema" value={form.tema} onChange={(e) => setForm({ ...form, tema: e.target.value })} className={campo} placeholder="Tema o pasaje bíblico" />
            </div>
          </div>
          {error && <p role="alert" className="mt-4 text-sm text-red-300">{error}</p>}
          <div className="mt-6 flex gap-3">
            <button onClick={guardar} disabled={ocupado} className={`${botonPrimario} flex-1`}>{ocupado ? "Guardando..." : "Guardar"}</button>
            <button onClick={() => setForm(null)} className={`${botonSecundario} flex-1`}>Cancelar</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Reuniones;
